import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomDataGrid from "./CustomDataGrid";
import useMaterial from "../../../../hooks/useMaterial";
import useInventory from "../../../../hooks/useInventory";
import { useDispatch } from "react-redux";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../../redux/slices/snackbar";
import { saveFile } from "../../../../utils/common";

function MaterialList() {
  const { getAllMaterial, materials, paging, isLoading } = useMaterial();
  const { exportInventoryReport } = useInventory();
  const dispatch = useDispatch();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadType, setDownloadType] = useState("");

  const { totalNumberOfRecords, hasNextPage, pageNumber, pageSize } = paging;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const currentPayload = {
    pagination: {
      pageNumber: 1,
      pageSize: 100,
    },
    filters: [],
    sortFields: [
      {
        field: "materialName",
        sort: "ASC",
      },
    ],
  };

  const [sortModel, setSortModel] = useState([
    {
      field: "materialName",
      sort: "asc",
    },
  ]);

  const [payload, setPayload] = useState(currentPayload);

  const [filterResetFunc, setFilterResetFunc] = useState(
    () => () => console.log("Refresh initialized"),
  );

  const fetchData = async () => {
    await getAllMaterial(payload);
  };

  const refreshPage = () => {
    setPayload(currentPayload);
    filterResetFunc();
    setPaginationModel({
      page: 0,
      pageSize: 100,
    });
  };

  const onPageChange = (newPaginationModel) => {
    const pagination = {
      pageNumber: newPaginationModel.page === 0 ? 1 : newPaginationModel.page + 1,
      pageSize: newPaginationModel.pageSize,
    };

    setPayload((prev) => ({ ...prev, pagination }));
    setPaginationModel(newPaginationModel);
  };

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 100,
  });

  const paginationMetaRef = useRef();

  const paginationMeta = React.useMemo(() => {
    if (hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== hasNextPage) {
      paginationMetaRef.current = { hasNextPage };
    }
    return paginationMetaRef.current;
  }, [hasNextPage]);

  useEffect(() => {
    fetchData();
  }, [payload]);

  const addMaterial = () => {
    navigate("/inventory-mgnt/material/add");
  };

  const editMaterial = (id) => {
    navigate("/inventory-mgnt/material/edit", { state: { id: id } });
  };

  const exportFile = async (fileType) => {
    setDownloadType(fileType);
    setIsDownloading(true);
    try {
      const resBlob = await exportInventoryReport("material-master", fileType, {
        filters: payload.filters || [],
      });
      await saveFile(
        resBlob,
        `material_master_${new Date().toISOString().slice(0, 10)}.${fileType}`,
      );
      dispatch(showSuccessSnackbar(`Exported as ${fileType.toUpperCase()} successfully!`));
    } catch (err) {
      dispatch(showErrorSnackbar("Failed to export"));
    } finally {
      setIsDownloading(false);
      setDownloadType("");
    }
  };

  const handleSortModelChange = (newModel) => {
    if (newModel.length !== 0) {
      setSortModel(newModel);
      setPayload((prev) => ({ ...prev, sortFields: newModel }));
    }
  };

  return (
    <React.Fragment>
      <CustomDataGrid
        trans={t}
        addPage={addMaterial}
        editPage={editMaterial}
        refreshPage={refreshPage}
        setFilterResetFunc={setFilterResetFunc}
        downloadFile={exportFile}
        id="materialId"
        isLoading={isLoading}
        sortModel={sortModel}
        handleSortModelChange={handleSortModelChange}
        payload={payload}
        setPayload={setPayload}
        rows={materials}
        rowCount={totalNumberOfRecords}
        onPaginationModelChange={onPageChange}
        paginationMeta={paginationMeta}
        paging={{
          pageNumber: pageNumber,
          pageSize: pageSize,
        }}
        paginationModel={paginationModel}
      />
    </React.Fragment>
  );
}

export default MaterialList;
